// pages/Dashboard.tsx
import { useEffect, useState, useMemo } from "react";
import { Home, Menu, Printer, Users } from "lucide-react";
import DashboardPortal from "./DashboardPortal";
import DashboardPersonalia from "./dashboard/Umum/Personalia/DashboardPersonalia";
import DashboardSegel from "./dashboard/Hublang/Penertiban/DashboardSegel";
import { PowerCircle } from "lucide-react";
import PersonaliaDataKaryawan from "./dashboard/Umum/Personalia/DataKaryawan/DataKaryawan";
import PersonaliaDataAbsensi from "./dashboard/Umum/Personalia/Absensi/DataAbsensi";
import TeknikPetaJaringan from "./dashboard/Teknik/PetaJaringan";
import PagePortal from "./PagePortal";
import DaftarTagihan from "./dashboard/Hublang/Penagihan-3/DaftarTagihan";
import Loket from "./dashboard/Loket"

export type Role = 'admin' | 'user';
export type Theme = 'light' | 'dark';

export interface AppConfig {
  id: string;
  name: string;
  desc: string;
  icon: string;
  color: string;
  adminOnly: boolean;
  tabColor: string;
}

export interface TabItem extends AppConfig {
    instanceId: string;
}

const COMPONENT_MAP: Record<string, (theme: Theme) => React.ReactNode> = {
    hrd: (theme) => <DashboardPortal SideBar={[
        { key : "dashboard",    name: "Dashboard", icon: <Home className="w-5 h-5 mr-3" />, element: DashboardPersonalia, level : ["05", "15", "07", "04", "09"] },
        { key : "data_karyawan", name: "Data Karyawan", icon: <Users className="w-5 h-5 mr-3" />, element: PersonaliaDataKaryawan, level : ["05", "15", "07", "04", "09"] },
        { key : "manaj_Absen",    name: "Manajemen Absensi", icon: <Users className="w-5 h-5 mr-3" />, element: PersonaliaDataAbsensi, level : ["05", "15", "07", "04", "09"] },
    ]} theme={theme} />,
    penagihan : (theme) => <PagePortal SideBar={[{ key : "penagihan", name : "Penagihan", icon: <Home className="w-5 h-5 mr-3" />, element: DaftarTagihan}]} />,
    finance: (theme) => <DashboardPortal SideBar={[]} theme={theme} />,
    hublang: (theme) => <DashboardPortal SideBar={[{ key : "segel", name : "Segel", icon: <Home className="w-5 h-5 mr-3" />, element: DashboardSegel}]} />,
    teknik: (theme) => <PagePortal SideBar={[{ key : "peta_jaringan", name : "Peta Jaringan", icon: <Home className="w-5 h-5 mr-3" />, element: TeknikPetaJaringan}]} />,
    loket: (theme) => <DashboardPortal SideBar={[{ key : "loket", name : "Lpp", icon: <Printer className="w-5 h-5 mr-3" />, element: Loket}]} />
    // Tambahkan komponen lain di sini
};

const APPS_PRIMARY: any = {
    hublang : [
        { id: 'hublang', name: 'Hubungan Langganan', desc: 'Hubungan & Langganan', icon: '🤝', color: '', adminOnly: false, tabColor: '#ffee00' },
        { id: 'penagihan', name: 'Penerimaan', desc : 'Penerimaan', icon : '💰', color: '', adminOnly: false, tabColor : '#ffd000' },
        
    ],
    umum_uang : [
        { id: 'hrd', name: 'Human Resource Management', desc: 'Umum & Keuangan', icon: '👥', color: 'green', adminOnly: false, tabColor: '#6200ff' },
        { id: 'finance', name: 'Keuangan & Akuntansi', desc: 'Umum & Keuangan', icon: '💹', color: 'violet', adminOnly: false, tabColor: '#00d4aa' },
        { id: 'loket', name: 'Loket', desc: 'Hublang', icon : '💰', color: '', adminOnly: false, tabColor : '#ffd000' },

    ],
    teknik : [
        { id: 'teknik', name: 'Admin Peta Jaringan', desc: 'Teknik', icon: '🗺️', color: 'blue', adminOnly : false, tabColor: '#003cff' }
    ]
};

export default function Portal() {
    const [theme, setTheme] = useState<Theme>('light');
    const [role, setRole] = useState<Role>('admin');
    const [isPortalOpen, setIsPortalOpen] = useState(true);
    const [tabs, setTabs] = useState<TabItem[]>([]);
    const [activeTabId, setActiveTabId] = useState<string | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [toast, setToast] = useState<{ msg: string; color: string } | null>(null);

    const isDark = theme === 'dark';

    // const filteredPrimary = useMemo(() => 
    //     APPS_PRIMARY.filter(app => app..name.toLowerCase()), 
    // []);

    const filteredPrimary = APPS_PRIMARY;

    const cardClass = theme === 'dark'
    ? 'bg-[#161b22] border-[#21262d] hover:border-white/20'
    : 'bg-white border-[#d0d7de] hover:border-blue-400 shadow-sm';

    const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');

    const showToast = (msg: string, color: string) => {
        setToast({ msg, color });
        setTimeout(() => setToast(null), 3000);
    };

    const openApp = (app: AppConfig) => {
        const existing = tabs.find(t => t.id === app.id);
        if (existing) {
            setActiveTabId(existing.instanceId);
            showToast(`Beralih ke ${app.name}`, app.tabColor);
        } else {
            const newTab = { ...app, instanceId: `tab_${Date.now()}` };
            setTabs([...tabs, newTab]);
            setActiveTabId(newTab.instanceId);
            showToast(`Membuka ${app.name}`, app.tabColor);
        }
        setIsPortalOpen(false);
    };

    const closeTab = (e: React.MouseEvent, instanceId: string) => {
        e.stopPropagation();
        const newTabs = tabs.filter(t => t.instanceId !== instanceId);
        setTabs(newTabs);
        if (newTabs.length === 0) {
        setIsPortalOpen(true);
        setActiveTabId(null);
        } else if (activeTabId === instanceId) {
        setActiveTabId(newTabs[newTabs.length - 1].instanceId);
        }
    };

    const s = {
        bg: isDark ? 'bg-[#0d1117]' : 'bg-[#f6f8fa]',
        text: isDark ? 'text-[#e6edf3]' : 'text-[#1f2328]',
        border: isDark ? 'border-[#21262d]' : 'border-[#d0d7de]',
        header: isDark ? 'bg-[#10161e]' : 'bg-[#ffffff]',
        tabActive: isDark ? 'bg-[#0d1117]' : 'bg-[#f6f8fa]',
        tabInactive: isDark ? 'bg-[#1c2330]' : 'bg-[#eaeff2]',
        card: isDark ? 'bg-[#161b22] border-[#21262d]' : 'bg-white border-[#d0d7de]',
    };

    const Section = ({ label, apps, onOpen, role }: { label: string, apps: AppConfig[], onOpen: (a: AppConfig) => void, role: Role }) => (
        <div className="mb-9">
            <span>{label}</span>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3.5">
                {apps.map((app, idx) => {
                    const isLocked = app.adminOnly && role !== 'admin';
                    return (
                        <div key={app.id} onClick={() => onOpen(app)} style={{ animationDelay: `${idx * 0.05}s` }} className={`group relative flex flex-col items-start gap-2.5 p-5 rounded-2xl ${cardClass} cursor-pointer transition-all hover:-translate-y-1 hover:scale-[1.02] hover:shadow-2xl active:scale-95 animate-in slide-in-from-bottom-4 fill-mode-both ${isLocked ? 'opacity-45 cursor-not-allowed hover:transform-none' : ''}`} >
                            <div className={`absolute top-0 left-0 right-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity`} style={{ background: `linear-gradient(90deg, ${app.tabColor}, #000)` }} />
                            {isLocked && <span className="absolute top-2.5 right-2.5 text-xs text-[#484f58]">🔒</span>}
                            
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl" style={{ backgroundColor: `${app.tabColor}1f` }}>
                                {app.icon}
                            </div>

                            <div>
                                <div className={`text-[13px] font-semibold leading-tight ${theme === 'dark' ? 'text-[#e6edf3]' : 'text-[#1f2328]'}`}>{app.name}</div>
                                <div className="text-[11px] text-[#7d8590] leading-relaxed mt-1">{app.desc}</div>
                                {app.adminOnly && (
                                    <div className="mt-2 text-[10px] px-2 py-0.5 rounded-full bg-[#00d4aa]/10 text-[#00d4aa] border border-[#00d4aa]/20 inline-block">
                                        Admin only
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return(
        <div className={`h-screen w-full flex flex-col transition-colors duration-300 ${s.bg} ${s.text}`}>
      
            <div className={`h-12 flex items-stretch px-2 gap-0.5 border-b shrink-0 z-50 ${s.header} ${s.border}`}>
                <button onClick={() => setIsPortalOpen(true)} className={`flex items-center gap-2 px-4 transition-all hover:opacity-80 ${isPortalOpen ? 'text-[#00d4aa] font-bold' : 'text-gray-500'}`} >
                    <span className="text-lg">⬛</span> 
                    <span className="text-xs uppercase tracking-tighter hidden md:block">Portal</span>
                </button>

                <div className="flex-1 flex items-stretch gap-1 overflow-x-auto no-scrollbar">
                    {tabs.map((tab) => (
                        <div key={tab.instanceId} onClick={() => { setActiveTabId(tab.instanceId); setIsPortalOpen(false); }} className={`relative mt-2 flex items-center min-w-[140px] rounded-t-md cursor-pointer transition-all border-x border-t ${activeTabId === tab.instanceId && !isPortalOpen ? `${s.tabActive} ${s.border} border-b-transparent z-10` : `${s.tabInactive} border-transparent opacity-60 hover:opacity-100`} `} >
                            <div className="absolute top-0 left-0 right-0 h-[2px] rounded-t-md" style={{ backgroundColor: tab.tabColor }} />
                            <div className="flex items-center gap-2 px-3 w-full">
                                <span className="text-sm">{tab.icon}</span>
                                <span className="text-[11px] font-bold truncate flex-1 uppercase tracking-tight">{tab.name}</span>
                                <button onClick={(e) => closeTab(e, tab.instanceId)} className="hover:text-red-500 text-xs">✕</button>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex items-center gap-3 px-4">
                    <button onClick={() => setTheme(isDark ? 'light' : 'dark')} className="text-sm grayscale hover:grayscale-0">
                        {isDark ? '☀️' : '🌙'}
                    </button>
                    <div className="relative">
                        {/* <button onClick={() => setDropdownOpen(!dropdownOpen)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors">
                            <span className="text-lg">👤</span>
                            <span className="text-sm font-medium">Admin</span>
                        </button> */}   
                        {/* {dropdownOpen && (
                            <div className={`absolute right-0 mt-2 w-48 rounded-lg border ${s.border} ${s.card} shadow-lg z-50`}>
                                <button className="w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors text-sm">
                                    👤 User Info
                                </button>
                                <div className={`h-px ${s.border}`} />
                                <button onClick={() => { setDropdownOpen(false); showToast('Logged out', '#00d4aa'); }} className="w-full text-left px-4 py-2.5 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-sm text-red-600 flex items-center gap-2">
                                    <PowerCircle size={16} /> Logout
                                </button>
                            </div>
                        )} */}
                    </div>
                </div>
            </div>

            <div className="flex-1 relative overflow-hidden">
                {/* --- PORTAL LAYER --- */}
                <div className={`absolute inset-0 z-10 flex flex-col transition-all duration-500 ${!isPortalOpen ? 'opacity-0 pointer-events-none scale-110 blur-lg' : 'opacity-100'}`}>
                    <div className="h-12 shrink-0" />
                    <main className="flex-1 overflow-y-auto px-6 md:px-12 pb-12 w-full">
                        <div className="max-w-6xl mx-auto">
                            <>
                                <Section label="Hubungan Langganan" apps={filteredPrimary.hublang} onOpen={openApp} role={role} />
                                <br />
                                <Section label="Aplikasi Umum & Keuangan" apps={filteredPrimary.umum_uang} onOpen={openApp} role={role} />
                                <br />
                                <Section label="Aplikasi Teknik" apps={filteredPrimary.teknik} onOpen={openApp} role={role} />
                            </>


                            {/* <br />
                            <Section label="* Aplikasi Utama" apps={filteredPrimary} onOpen={openApp} role={role} />
                            <Section label="📌 Aplikasi Utama" apps={filteredPrimary} onOpen={openApp} role={role} /> */}
                        </div>
                    </main>
                </div>

                {/* --- TAB LAYER --- */}
                <div className={`absolute inset-0 z-20 flex flex-col transition-all duration-400 ${isPortalOpen ? 'opacity-0 pointer-events-none translate-y-10' : 'opacity-100 translate-y-0'}`}>
                    {tabs.length === 0 && ( <div className="px-4 text-[11px] text-[#484f58] italic">Tidak ada aplikasi terbuka</div> )}

                    <div className={`absolute inset-0 z-10 overflow-y-auto transition-opacity duration-300 ${ !isPortalOpen ? 'opacity-100' : 'opacity-0 pointer-events-none' }`}>
                        {tabs.map((tab) => (
                            <div key={tab.instanceId} className={`h-full w-full ${activeTabId === tab.instanceId ? 'block' : 'hidden'}`}> 
                                {COMPONENT_MAP[tab.id]?.(theme) || ( <div className="p-20 text-center"> <h2 className="text-2xl">Menu "{tab.name}" sedang dalam pengembangan.</h2> </div> )}
                            </div>
                        ))}
                    </div>
                </div>

            </div>

            {/* --- TOAST --- */}
            {toast && (
                <div className="fixed bottom-7 left-1/2 -translate-x-1/2 z-[999] bg-[#161b22] border border-[#21262d] rounded-xl px-5 py-2.5 flex items-center gap-3 shadow-2xl animate-bounce-in">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: toast.color }} />
                    <span className="text-sm text-[#7d8590]">{toast.msg}</span>
                </div>
            )}
        </div>
    );
}
